// src/components/resumo/annotations/CommentsMargin.tsx
// Sistema de comentários estilo Microsoft Word na margem direita

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Trash2, X, Check, Pencil } from 'lucide-react';
import { Comment } from './annotations.types';

interface CommentCardPosition {
    id: string;
    top: number;       // final top (viewport Y, pushed down for anti-overlap)
    anchorTop: number; // original anchor top (viewport Y of the span)
}

interface CommentsMarginProps {
    comments: Comment[];
    pageId: string;
    onRemoveComment: (id: string) => void;
    onUpdateComment: (id: string, data: { comment: string; title?: string }) => void;
    activeCommentId: string | null;
    onSetActiveComment: (id: string | null) => void;
    darkMode?: boolean;
    contentRef: React.RefObject<HTMLElement | null>;
}

const CARD_HEIGHT = 130;
const CARD_GAP = 10;
const MARGIN_WIDTH = 272;

const CommentsMargin: React.FC<CommentsMarginProps> = ({
    comments,
    pageId,
    onRemoveComment,
    onUpdateComment,
    activeCommentId,
    onSetActiveComment,
    darkMode = false,
    contentRef,
}) => {
    const [positions, setPositions] = useState<CommentCardPosition[]>([]);
    const marginRef = useRef<HTMLDivElement>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editText, setEditText] = useState('');

    const pageComments = comments.filter(c => c.pageId === pageId);

    const recalcPositions = useCallback(() => {
        const rawPositions: CommentCardPosition[] = [];

        pageComments.forEach(comment => {
            const span = document.querySelector(
                `[data-comment-id="${comment.id}"]`
            ) as HTMLElement | null;

            if (!span) return;

            const rect = span.getBoundingClientRect();
            const anchorTop = rect.top + rect.height / 2;
            rawPositions.push({ id: comment.id, top: anchorTop, anchorTop });
        });

        // Só atualizar se encontrou pelo menos um span OU se não há comentários.
        // Evita limpar posições quando spans somem temporariamente (re-render por grifo).
        if (rawPositions.length === 0 && pageComments.length > 0) return;

        rawPositions.sort((a, b) => a.anchorTop - b.anchorTop);

        const adjusted: CommentCardPosition[] = [];
        rawPositions.forEach((pos, idx) => {
            let top = pos.anchorTop;
            if (idx > 0) {
                const prev = adjusted[idx - 1];
                const minTop = prev.top + CARD_HEIGHT + CARD_GAP;
                if (top < minTop) top = minTop;
            }
            adjusted.push({ ...pos, top });
        });

        setPositions(adjusted);
    }, [pageComments]);

    useEffect(() => {
        recalcPositions();
        const retry1 = setTimeout(recalcPositions, 200);
        const retry2 = setTimeout(recalcPositions, 800);
        window.addEventListener('scroll', recalcPositions, { passive: true });
        window.addEventListener('resize', recalcPositions);
        // Re-calcular quando AnnotationLayer re-aplica marks após dangerouslySetInnerHTML
        const handleMarksApplied = () => setTimeout(recalcPositions, 50);
        window.addEventListener('comment-marks-applied', handleMarksApplied);
        return () => {
            clearTimeout(retry1);
            clearTimeout(retry2);
            window.removeEventListener('scroll', recalcPositions);
            window.removeEventListener('resize', recalcPositions);
            window.removeEventListener('comment-marks-applied', handleMarksApplied);
        };
    }, [recalcPositions]);

    // Visible comments = those whose span was found in DOM
    const visibleComments = pageComments.filter(c => positions.find(p => p.id === c.id));

    if (visibleComments.length === 0) return null;

    // SVG height: enough to cover the lowest card
    const svgHeight = Math.max(window.innerHeight, ...(positions.map(p => p.top + CARD_HEIGHT + 20)));

    return (
        <div
            ref={marginRef}
            className={`comments-margin ${darkMode ? 'dark' : ''}`}
            style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: `${MARGIN_WIDTH}px`,
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 200,
                overflow: 'visible',
            }}
        >
            {/* ── SVG overlay with intelligent connector lines ── */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: svgHeight,
                    overflow: 'visible',
                    pointerEvents: 'none',
                }}
            >
                {positions.map(pos => {
                    const isActive = activeCommentId === pos.id;
                    const ax = 0;                     // left edge of margin
                    const ay = pos.anchorTop;         // anchor viewport Y
                    const cx = 12;                    // card left edge
                    const cy = pos.top + 18;          // card top + vertical offset

                    let d: string;
                    if (Math.abs(ay - cy) < 8) {
                        // Same level: simple horizontal line
                        d = `M ${ax} ${ay} L ${cx} ${cy}`;
                    } else {
                        // Different level: L-shaped path (elbow connector like Word)
                        const elbowX = 28;
                        d = `M ${ax} ${ay} H ${elbowX} V ${cy} H ${cx}`;
                    }

                    return (
                        <path
                            key={pos.id}
                            d={d}
                            stroke={isActive ? 'var(--platform-accent)' : '#b0b8c1'}
                            strokeWidth={isActive ? 2 : 1.5}
                            strokeDasharray={isActive ? '' : '5,3'}
                            fill="none"
                            strokeLinejoin="round"
                        />
                    );
                })}
            </svg>

            {/* ── Comment cards ── */}
            {visibleComments.map(comment => {
                const pos = positions.find(p => p.id === comment.id);
                const top = pos?.top ?? 0;
                const isActive = activeCommentId === comment.id;

                return (
                    <div
                        key={comment.id}
                        className={`comment-card ${isActive ? 'active' : ''} ${darkMode ? 'dark' : ''}`}
                        style={{
                            position: 'absolute',
                            top: `${top}px`,
                            left: '12px',
                            width: `${MARGIN_WIDTH - 24}px`,
                            pointerEvents: 'auto',
                            transform: 'translateY(-18px)', // center card on connector
                        }}
                        onClick={() => onSetActiveComment(isActive ? null : comment.id)}
                    >
                        <div className="comment-card-header">
                            <span className="comment-author">
                                {comment.title || comment.authorName || 'Comentário'}
                            </span>
                            <span className="comment-time">
                                {new Date(comment.createdAt).toLocaleDateString('pt-BR', {
                                    day: '2-digit', month: '2-digit'
                                })}
                            </span>
                            <button
                                className="comment-delete-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingId(null);
                                    onRemoveComment(comment.id);
                                }}
                                title="Excluir comentário"
                            >
                                <Trash2 size={12} />
                            </button>
                            <button
                                className="comment-delete-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingId(comment.id);
                                    setEditTitle(comment.title || comment.authorName || '');
                                    setEditText(comment.comment);
                                }}
                                title="Editar comentário"
                                style={{ marginRight: 2 }}
                            >
                                <Pencil size={12} />
                            </button>
                        </div>

                        {editingId === comment.id ? (
                            <div onClick={e => e.stopPropagation()} style={{ paddingTop: 4 }}>
                                <input
                                    value={editTitle}
                                    onChange={e => setEditTitle(e.target.value)}
                                    placeholder="Título (opcional)"
                                    autoFocus
                                    style={{ width: '100%', fontSize: 11, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 6px', marginBottom: 4, fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                />
                                <textarea
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    rows={2}
                                    style={{ width: '100%', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 6px', fontFamily: 'inherit', resize: 'vertical' as const, boxSizing: 'border-box' as const }}
                                />
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 4 }}>
                                    <button
                                        onClick={() => setEditingId(null)}
                                        style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >Cancelar</button>
                                    <button
                                        onClick={() => {
                                            if (editText.trim()) {
                                                onUpdateComment(comment.id, {
                                                    comment: editText.trim(),
                                                    title: editTitle.trim() || undefined,
                                                });
                                                setEditingId(null);
                                            }
                                        }}
                                        style={{ fontSize: 10, padding: '2px 8px', border: 'none', borderRadius: 4, background: 'var(--platform-accent)', color: 'white', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >Salvar</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="comment-selected-text">
                                    "{comment.selectedText.length > 40
                                        ? comment.selectedText.substring(0, 40) + '...'
                                        : comment.selectedText}"
                                </div>
                                <div className="comment-text">{comment.comment}</div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ========== MODAL PARA ADICIONAR COMENTÁRIO ==========
interface CommentInputModalProps {
    position: { x: number; y: number };
    selectedText: string;
    onSave: (text: string, title: string) => void;
    onCancel: () => void;
    darkMode?: boolean;
}

export const CommentInputModal: React.FC<CommentInputModalProps> = ({
    position,
    selectedText,
    onSave,
    onCancel,
    darkMode = false,
}) => {
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const titleRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTimeout(() => titleRef.current?.focus(), 50);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (text.trim()) onSave(text.trim(), title.trim());
        }
        if (e.key === 'Escape') onCancel();
    };

    const adjustedX = Math.min(position.x, window.innerWidth - 290);
    const adjustedY = Math.min(position.y, window.innerHeight - 260);

    return (
        <div
            className={`comment-input-modal ${darkMode ? 'dark' : ''}`}
            style={{
                position: 'fixed',
                left: adjustedX,
                top: adjustedY,
                zIndex: 50000,
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="cim-header">
                <span className="cim-title">Comentário</span>
                <button className="cim-close" onClick={onCancel}><X size={14} /></button>
            </div>
            <div className="cim-selected-preview">
                "{selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText}"
            </div>
            <input
                ref={titleRef}
                className="cim-textarea"
                placeholder="Título (opcional)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ borderBottom: 'none', padding: '8px 12px 6px', fontSize: 12, fontWeight: 600 }}
            />
            <textarea
                className="cim-textarea"
                placeholder="Escreva seu comentário... (Ctrl+Enter para salvar)"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
            />
            <div className="cim-actions">
                <button className="cim-cancel" onClick={onCancel}>Cancelar</button>
                <button
                    className="cim-save"
                    onClick={() => { if (text.trim()) onSave(text.trim(), title.trim()); }}
                    disabled={!text.trim()}
                >
                    <Check size={14} />
                    Salvar
                </button>
            </div>
        </div>
    );
};

export default CommentsMargin;
